import React, { useState, useEffect } from 'react';
import {
  Box, Tabs, Tab, Paper, Typography, FormControl, InputLabel,
  Select, MenuItem, Stack, Snackbar, Alert, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, IconButton, Tooltip,
} from '@mui/material';
import { Add as AddIcon, Language as LanguageIcon } from '@mui/icons-material';
import { GlobalPromptEditor } from '../../components/GlobalPromptEditor';
import CPTPromptManager from '../../components/CPTPromptManager';
import { promptService } from '../../services/promptService';
import PageHeaderNavigation from '../../components/common/PageHeaderNavigation';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`prompt-tabpanel-${index}`}
      aria-labelledby={`prompt-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const DEFAULT_LOCALE = 'en-US';

export const PromptManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedLocale, setSelectedLocale] = useState(DEFAULT_LOCALE);
  const [locales, setLocales] = useState<string[]>([DEFAULT_LOCALE]);
  const [addLocaleOpen, setAddLocaleOpen] = useState(false);
  const [newLocaleCode, setNewLocaleCode] = useState('');
  const [addLocaleError, setAddLocaleError] = useState<string | null>(null);
  const [addingLocale, setAddingLocale] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false, message: '', severity: 'info',
  });

  const loadLocales = async () => {
    try {
      const data = await promptService.listLocales();
      const merged = Array.from(new Set([DEFAULT_LOCALE, ...data.locales])).sort();
      setLocales(merged);
    } catch {
      // keep default
    }
  };

  useEffect(() => {
    loadLocales();
  }, []);

  const handleSnack = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnack({ open: true, message, severity });
  };

  const handleAddLocale = async () => {
    const code = newLocaleCode.trim();
    if (!code) { setAddLocaleError('Locale code is required'); return; }
    if (!/^[a-zA-Z]{2,8}(-[a-zA-Z0-9]{2,8})*$/.test(code)) {
      setAddLocaleError('Use BCP-47 format e.g. fr-FR, de-DE, pt-BR');
      return;
    }
    if (locales.includes(code)) { setAddLocaleError('Locale already exists'); return; }
    try {
      setAddingLocale(true);
      setAddLocaleError(null);
      await promptService.createLocale(code);
      await loadLocales();
      setSelectedLocale(code);
      setAddLocaleOpen(false);
      setNewLocaleCode('');
      handleSnack(`Locale '${code}' created — now editing its prompt`, 'success');
      setActiveTab(0);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setAddLocaleError(axiosErr.response?.data?.detail || 'Failed to create locale');
    } finally {
      setAddingLocale(false);
    }
  };

  const localeLabel = (code: string) => {
    try { return new Intl.DisplayNames(['en'], { type: 'language' }).of(code) ?? code; }
    catch { return code; }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <PageHeaderNavigation
        title="Prompt Management"
        subtitle="Manage global report prompts and CPT-specific addendums"
      />

        {/* Tabs */}
        <Paper>
          <Tabs
            value={activeTab}
            onChange={(_e, v) => setActiveTab(v)}
            aria-label="prompt management tabs"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Global Prompt" id="prompt-tab-0" aria-controls="prompt-tabpanel-0" />
            <Tab label="CPT Prompts" id="prompt-tab-1" aria-controls="prompt-tabpanel-1" />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            {/* Locale selector row */}
            <Box sx={{ px: 2, pb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <LanguageIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  Editing prompt for:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="locale-select-label">Locale</InputLabel>
                  <Select
                    labelId="locale-select-label"
                    value={selectedLocale}
                    label="Locale"
                    onChange={(e) => setSelectedLocale(e.target.value)}
                  >
                    {locales.map((code) => (
                      <MenuItem key={code} value={code}>
                        {code} — {localeLabel(code)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Add a new locale">
                  <IconButton size="small" onClick={() => { setNewLocaleCode(''); setAddLocaleError(null); setAddLocaleOpen(true); }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
            <GlobalPromptEditor
              locale={selectedLocale}
              onLocaleCreated={(code) => {
                loadLocales();
                handleSnack(`Locale-specific prompt for '${code}' saved`, 'success');
              }}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <CPTPromptManager onSnack={handleSnack} />
          </TabPanel>
        </Paper>

      {/* Add Locale Dialog */}
      <Dialog open={addLocaleOpen} onClose={() => setAddLocaleOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add New Locale</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Enter a BCP-47 locale tag. The default global prompt will be copied as the starting point.
              You can then customise it in the editor.
            </Typography>
            <TextField
              label="Locale code"
              placeholder="e.g. fr-FR, de-DE, pt-BR"
              value={newLocaleCode}
              onChange={(e) => { setNewLocaleCode(e.target.value); setAddLocaleError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddLocale(); }}
              error={!!addLocaleError}
              helperText={addLocaleError ?? 'Examples: fr-FR · de-DE · es-ES · pt-BR · ja-JP'}
              fullWidth
              autoFocus
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setAddLocaleOpen(false)} disabled={addingLocale}>Cancel</Button>
          <Button size="small" variant="contained" onClick={handleAddLocale} disabled={addingLocale || !newLocaleCode.trim()}>
            {addingLocale ? 'Creating…' : 'Create locale'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PromptManagementPage;
